import { useQuery } from "@tanstack/react-query"
import { OverviewContext, type MembersBoardOverview } from "@ibada/shared-types/overview"

import { http } from "@/shared/lib/api/http"

/** ADR-0003 success envelope. */
type Envelope<T> = { data?: T | null }

export type MembersBoardContext = OverviewContext.Branch | OverviewContext.Parish

export type MembersBoardQuery = {
  workspaceId: string
  context: MembersBoardContext
  /** The branch on the branch board; ignored on the parish board. */
  branchId?: string
}

/** `GET /members/overview/board`: the members board tiles. Scope and permissions are decided server-side. */
export async function fetchMembersBoard(query: MembersBoardQuery): Promise<MembersBoardOverview> {
  const params =
    query.context === OverviewContext.Parish
      ? { workspaceId: query.workspaceId, context: query.context }
      : { workspaceId: query.workspaceId, context: query.context, branchId: query.branchId }
  const response = await http.get<Envelope<MembersBoardOverview>>("/members/overview/board", { params })
  const data = response.data?.data
  if (!data) throw new Error("Members board: empty response")
  return data
}

/**
 * Under `["members", "overview"]`, so every member mutation (confirm, edit, create) refreshes the board. Workspace,
 * context AND branch are in the key: a parish board and a branch board never share an entry.
 */
export const membersBoardQueryKeys = {
  board: (workspaceId: string | null | undefined, context: MembersBoardContext, branchId: string | null | undefined) =>
    [
      "members",
      "overview",
      "board",
      workspaceId ?? "__none__",
      context,
      context === OverviewContext.Parish ? "__parish__" : branchId ?? "__none__",
    ] as const,
}

export function useMembersBoardQuery(
  scope: { workspaceId: string | null | undefined; context: MembersBoardContext; branchId: string | null | undefined },
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: membersBoardQueryKeys.board(scope.workspaceId, scope.context, scope.branchId),
    queryFn: () =>
      fetchMembersBoard({
        workspaceId: scope.workspaceId ?? "",
        context: scope.context,
        branchId: scope.branchId ?? undefined,
      }),
    enabled:
      options.enabled &&
      Boolean(scope.workspaceId) &&
      (scope.context === OverviewContext.Parish || Boolean(scope.branchId)),
    // A refused read (403) will be refused again: the tiles hide instead of retrying.
    retry: false,
  })
}
